import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { checkWebPushPermission } from './web-push-permission-check';

type WebPushPermissionGuideProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function WebPushPermissionGuide({
  isOpen,
  onClose,
}: WebPushPermissionGuideProps) {
  const [isStillNotAllowed, setIsStillNotAllowed] = useState(false);
  const shouldCloseAfterGrant = useRef(false);

  const close = () => {
    setIsStillNotAllowed(false);
    onClose();
  };

  const checkPermission = () => {
    checkWebPushPermission();
    if (window.Notification?.permission === 'granted') {
      close();
      return;
    }
    setIsStillNotAllowed(true);
  };

  useEffect(() => {
    if (!isOpen) {
      shouldCloseAfterGrant.current = false;
      return;
    }
    if (window.Notification?.permission !== 'granted') {
      shouldCloseAfterGrant.current = true;
    }

    const closeWhenGranted = () => {
      if (
        !shouldCloseAfterGrant.current ||
        window.Notification?.permission !== 'granted'
      ) {
        return;
      }
      shouldCloseAfterGrant.current = false;
      checkWebPushPermission();
      setIsStillNotAllowed(false);
      onClose();
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') closeWhenGranted();
    };
    let permissionStatus: PermissionStatus | null = null;
    let isDisposed = false;

    void navigator.permissions
      ?.query({ name: 'notifications' })
      .then((status) => {
        if (isDisposed) return;
        permissionStatus = status;
        status.addEventListener('change', closeWhenGranted);
        closeWhenGranted();
      })
      .catch(() => undefined);

    window.addEventListener('focus', closeWhenGranted);
    document.addEventListener('visibilitychange', checkWhenVisible);
    closeWhenGranted();
    return () => {
      isDisposed = true;
      permissionStatus?.removeEventListener('change', closeWhenGranted);
      window.removeEventListener('focus', closeWhenGranted);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [isOpen, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={close} isCentered>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader>Izinkan notifikasi di browser</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="start" spacing={3} fontSize="sm">
            <Text>
              Notifikasi situs ini belum diizinkan. Ubah izin untuk situs ini di
              pengaturan browser Anda.
            </Text>
            <Text>
              <strong>Chrome atau Edge:</strong> klik ikon di kiri alamat situs
              (atau “Notifications blocked”), buka pengaturan situs, lalu ubah
              Notifikasi menjadi Izinkan.
            </Text>
            <Text>
              <strong>Firefox:</strong> klik ikon izin di kiri alamat situs,
              lalu ubah izin Kirim Notifikasi menjadi Izinkan.
            </Text>
            <Text>
              <strong>Safari di Mac:</strong> buka Safari &gt; Pengaturan &gt;
              Situs Web &gt; Notifikasi, lalu izinkan situs ini.
            </Text>
            <Text>
              Jika situs sudah diizinkan tetapi notifikasi belum muncul di
              Windows, periksa Pengaturan Windows &gt; Sistem &gt; Notifikasi
              dan izinkan browser Anda.
            </Text>
            {isStillNotAllowed ? (
              <Text color="orange.700" role="status">
                Browser masih belum mengizinkan notifikasi untuk situs ini.
              </Text>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={close}>
            Tutup
          </Button>
          <Button colorScheme="blue" onClick={checkPermission}>
            Periksa lagi
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
